import type { EnhancedDeal, Lead, TimberProject } from '../types';
import { compareTimberProjectsByDealMap } from './projectNumberSort';

export const CONTRACTOR_OTHER_PROJECT_ID = '__contractor_other__';
export const CONTRACTOR_OTHER_PROJECT_LABEL = 'Other — non-project / overhead';

export type ContractorProjectOption = {
  id: string;
  label: string;
  /** Lowercase terms for picker search only — not shown in the UI. */
  searchText: string;
  projectNumber?: string;
  name: string;
  clientLabel?: string;
  descriptor?: string;
};

function leadDisplayName(lead: Lead | undefined): string {
  if (!lead) return '';
  return [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
}

/** Search-only aliases (lead / client / owner) — labels stay project-centric. */
export function buildContractorProjectSearchText(
  project: TimberProject,
  deal: EnhancedDeal | undefined,
  lead: Lead | undefined,
  label: string,
): string {
  const leadName = leadDisplayName(lead);
  return [
    label,
    deal?.projectNumber,
    deal?.clientLabel,
    deal?.descriptor,
    deal?.contactName,
    deal?.company,
    deal?.title,
    project.name,
    project.clientName,
    leadName,
    lead?.company,
    lead?.enrichment?.person?.fullName,
    lead?.enrichment?.ownership?.ownerName,
    project.id,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function projectLabel(project: TimberProject, deal: EnhancedDeal | undefined): string {
  const num = deal?.projectNumber?.trim();
  const numPart = num ? `#${num.replace(/^#+/, '')}` : '';
  const client = (deal?.clientLabel || deal?.descriptor || project.clientName || '').trim();
  const name = (project.name || '').trim();
  const head = [numPart, client].filter(Boolean).join(' ');
  if (head && name) return `${head} — ${name}`;
  return head || name || project.id || 'Project';
}

/** Timber projects available for contractor hour logging (includes completed). */
export function buildContractorProjectOptions(
  timberProjects: TimberProject[],
  enhancedDeals: EnhancedDeal[],
  allowedProjectIds?: string[] | null,
  leads: Lead[] = [],
): ContractorProjectOption[] {
  const dealsById = new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d] as const));
  const leadsById = new Map(leads.filter((l) => l.id).map((l) => [l.id!, l] as const));
  const allowSet =
    allowedProjectIds && allowedProjectIds.length > 0 ? new Set(allowedProjectIds) : null;

  const rows = timberProjects.filter((p) => {
    if (!p.id) return false;
    if (allowSet && !allowSet.has(p.id)) return false;
    return true;
  });

  rows.sort((a, b) => compareTimberProjectsByDealMap(a, b, dealsById));

  return rows.map((p) => {
    const deal = p.dealId ? dealsById.get(p.dealId) : undefined;
    const lead = deal?.leadId ? leadsById.get(deal.leadId) : undefined;
    const baseLabel = projectLabel(p, deal);
    const label = p.status === 'completed' ? `${baseLabel} (Completed)` : baseLabel;
    return {
      id: p.id!,
      label,
      searchText: buildContractorProjectSearchText(p, deal, lead, label),
      projectNumber: deal?.projectNumber,
      name: p.name || '',
      clientLabel: deal?.clientLabel,
      descriptor: deal?.descriptor,
    };
  });
}

export function contractorProjectEntryLabel(
  projectId: string,
  options: ContractorProjectOption[],
): string {
  if (projectId === CONTRACTOR_OTHER_PROJECT_ID) return CONTRACTOR_OTHER_PROJECT_LABEL;
  const proj = options.find((p) => p.id === projectId);
  return proj?.label || projectId;
}

export function filterContractorProjectOptions(
  options: ContractorProjectOption[],
  query: string,
): ContractorProjectOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.searchText.includes(q));
}
